import { create } from 'zustand';
import api from '../services/api';

export interface FlowchartNode {
  id: string;
  type: string;
  position: {
    x: number;
    y: number;
  };
  data: {
    label: string;
    [key: string]: any;
  };
  style?: {
    backgroundColor?: string;
    borderColor?: string;
    borderWidth?: number;
    [key: string]: any;
  };
}

export interface FlowchartEdge {
  id: string;
  source: string;
  target: string;
  label?: string;
  style?: {
    stroke?: string;
    strokeWidth?: number;
    strokeDasharray?: string;
    [key: string]: any;
  };
}

export interface FlowchartData {
  nodes: FlowchartNode[];
  edges: FlowchartEdge[];
  viewport: {
    x: number;
    y: number;
    zoom: number;
  };
}

export interface Flowchart {
  id: number;
  title: string;
  slug: string;
  folder_path: string;
  file_path: string;
  created_by: number | null;
  is_private: boolean;
  created_at: string;
  updated_at: string;
}

export interface FlowchartWithContent extends Flowchart {
  content?: FlowchartData;
}

interface FlowchartState {
  flowcharts: Flowchart[];
  currentFlowchart: FlowchartWithContent | null;
  syncStatus: 'idle' | 'syncing' | 'error';
  error: string | null;
  
  // Actions
  fetchFlowcharts: (folderPath?: string) => Promise<void>;
  fetchFlowchart: (slug: string) => Promise<void>;
  createFlowchart: (data: { title: string; folder_path?: string; is_private?: boolean }) => Promise<Flowchart>;
  saveFlowchartContent: (id: number, content: FlowchartData) => Promise<void>;
  updateFlowchart: (id: number, data: Partial<Flowchart>) => Promise<void>;
  deleteFlowchart: (id: number) => Promise<void>;
  clearCurrentFlowchart: () => void;
}

export const useFlowchartStore = create<FlowchartState>((set) => ({
  flowcharts: [],
  currentFlowchart: null,
  syncStatus: 'idle',
  error: null,

  fetchFlowcharts: async (folderPath?: string) => {
    try {
      const params = folderPath ? { params: { folder: folderPath } } : {};
      const response = await api.get('/flowcharts', params);
      set({ flowcharts: response.data });
    } catch (error: any) {
      console.error('Error fetching flowcharts:', error);
      set({ error: error.message });
    }
  },

  fetchFlowchart: async (slug: string) => {
    try {
      const response = await api.get(`/flowcharts/${slug}`);
      const flowchart = response.data;
      
      // Fetch content separately
      const contentResponse = await api.get(`/flowcharts/${slug}/embed`);
      const content = contentResponse.data.content;
      
      set({ 
        currentFlowchart: {
          ...flowchart,
          content
        }
      });
    } catch (error: any) {
      console.error('Error fetching flowchart:', error);
      set({ error: error.message });
    }
  },

  createFlowchart: async (data: { title: string; folder_path?: string; is_private?: boolean }) => {
    try {
      const response = await api.post('/flowcharts', {
        ...data,
        content: {
          nodes: [],
          edges: [],
          viewport: { x: 0, y: 0, zoom: 1 }
        }
      });
      
      const newFlowchart = response.data;
      set((state) => ({
        flowcharts: [...state.flowcharts, newFlowchart],
        currentFlowchart: {
          ...newFlowchart,
          content: {
            nodes: [],
            edges: [],
            viewport: { x: 0, y: 0, zoom: 1 }
          }
        }
      }));
      
      return newFlowchart;
    } catch (error: any) {
      console.error('Error creating flowchart:', error);
      set({ error: error.message });
      throw error;
    }
  },

  saveFlowchartContent: async (id: number, content: FlowchartData) => {
    try {
      set({ syncStatus: 'syncing' });
      await api.put(`/flowcharts/${id}/content`, { content });
      set({ syncStatus: 'idle' });
    } catch (error: any) {
      console.error('Error saving flowchart content:', error);
      set({ syncStatus: 'error', error: error.message });
      throw error;
    }
  },

  updateFlowchart: async (id: number, data: Partial<Flowchart>) => {
    try {
      const response = await api.put(`/flowcharts/${id}`, data);
      const updatedFlowchart = response.data;
      
      set((state) => ({
        flowcharts: state.flowcharts.map((f) => 
          f.id === id ? updatedFlowchart : f
        ),
        currentFlowchart: state.currentFlowchart?.id === id 
          ? { ...state.currentFlowchart, ...updatedFlowchart }
          : state.currentFlowchart
      }));
    } catch (error: any) {
      console.error('Error updating flowchart:', error);
      set({ error: error.message });
      throw error;
    }
  },

  deleteFlowchart: async (id: number) => {
    try {
      await api.delete(`/flowcharts/${id}`);
      set((state) => ({
        flowcharts: state.flowcharts.filter((f) => f.id !== id),
        currentFlowchart: state.currentFlowchart?.id === id ? null : state.currentFlowchart
      }));
    } catch (error: any) {
      console.error('Error deleting flowchart:', error);
      set({ error: error.message });
      throw error;
    }
  },

  clearCurrentFlowchart: () => {
    set({ currentFlowchart: null });
  }
}));
